import React, { useState } from "react";
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Modal,
  useMediaQuery,
  Stack,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "./Dashboard.css";
import { useNavigate } from "react-router-dom";

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#3f434d", // P1
      light: "#63c2e6", // P2
    },
    secondary: {
      main: "#7cbf60", // S1
      light: "#f1a94e", // S2
    },
    neutral: {
      main: "#191b20", // N1
      dark: "#000", // N2
      light: "#fff", // N3
    },
    accent: {
      main: "#9d7fe9", // A1
      light: "#ff6b6b", // A2
    },
  },
});

// Styled components
const LargeButton = styled(Button)(({ theme }) => ({
  fontSize: "1.2rem",
  padding: theme.spacing(2, 4),
  margin: theme.spacing(2),
  width: "100%",
  maxWidth: "400px",
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.neutral.light,
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  "& .MuiTableCell-head": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.neutral.light,
  },
  "& .MuiTableCell-body": {
    color: theme.palette.neutral.main,
  },
}));

function Dashboard({ user, laravelApiToken, loadingToken, handleSignOut }) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const itemsPerPage = 5; // You can adjust this value as needed

  const [searchResults, setSearchResults] = useState([]);
  const [searchEmail, setSearchEmail] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [selectedHackId, setSelectedHackId] = useState(null);

  const [recentSearches, setRecentSearches] = useState([
    { name: "John Doe", date: "2023-04-15" },
    { name: "Jane Smith", date: "2023-04-14" },
    // Add more mock data as needed
  ]);

  const [activeSubscriptions, setActiveSubscriptions] = useState([
    { name: "Subscription 1", activeUntil: "2023-05-15" },
    { name: "Subscription 2", activeUntil: "2023-06-01" },
    // Add more mock data as needed
  ]);

  const handleEmailSearch = async () => {
    try {
      const response = await fetch(
        `http://api.hackerthread.com/api/people/search?email1=${searchEmail}`
      );
      const data = await response.json();
      setSearchResults(data);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  };

  const totalPages = Math.ceil(searchResults.length / itemsPerPage);

  const paginatedResults = searchResults.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleBuyReport = (hackInfoId) => {
    setSelectedHackId(hackInfoId);
    setOpenModal(true);
  };

  const renderSearchResultRow = (item) => (
    <TableRow key={item.hackDetails.hack_info.id}>
      <TableCell>{item.people.name}</TableCell>
      <TableCell>{item.people.email1}</TableCell>
      <TableCell>{item.hackDetails.title}</TableCell>
      <TableCell>{item.hackDetails.type}</TableCell>
      <TableCell>{item.hackDetails.hack_date || "Date Unknown"}</TableCell>
      <TableCell>{item.hackDetails.leak_date || "Date Unknown"}</TableCell>
      <TableCell>{item.hackDetails.description}</TableCell>
      <TableCell>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleBuyReport(item.hackDetails.hack_info.id)}
        >
          Buy Report
        </Button>
      </TableCell>
    </TableRow>
  );

  const renderPagination = (currentPage, totalPages, setCurrentPage) => (
    <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
      <IconButton
        onClick={() => setCurrentPage(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeftIcon />
      </IconButton>
      <Typography variant="body2" mx={2}>
        {`${currentPage} / ${totalPages}`}
      </Typography>
      <IconButton
        onClick={() => setCurrentPage(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );

  const renderListView = (items, fields, itemsPerPage, currentPage) => {
    const paginatedItems = items.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

    return (
      <StyledTableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {fields.map((field) => (
                <TableCell key={field}>{field}</TableCell>
              ))}
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedItems.map((item, index) => (
              <TableRow key={index}>
                {fields.map((field) => (
                  <TableCell key={field}>{item[field.toLowerCase()]}</TableCell>
                ))}
                <TableCell>
                  <Button
                    variant="contained"
                    color="secondary"
                    href="#"
                    target="_blank"
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
    );
  };

  const handleSearchByEmail = () => {
    navigate("/search", { state: { searchType: "email" } });
  };

  const handleSearchByPhone = () => {
    navigate("/search", { state: { searchType: "phone" } });
  };

  return (
    <ThemeProvider theme={theme}>
      <Container sx={{ minHeight: "100vh", overflow: "auto" }}>
        <Box my={4}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <LargeButton variant="contained" onClick={handleSearchByEmail}>
              Search By Email
            </LargeButton>
            <LargeButton variant="contained" onClick={handleSearchByPhone}>
              Search By Phone Number
            </LargeButton>
          </Stack>
        </Box>

        <Box my={4}>
          <Typography variant="h5" gutterBottom color="primary">
            Recent Searches
          </Typography>
          {renderListView(
            recentSearches,
            ["Name", "Date"],
            itemsPerPage,
            currentPage
          )}
          {renderPagination(
            currentPage,
            Math.ceil(recentSearches.length / itemsPerPage),
            setCurrentPage
          )}
        </Box>

        <Box my={4}>
          <Typography variant="h5" gutterBottom color="primary">
            Active Subscriptions
          </Typography>
          {renderListView(
            activeSubscriptions,
            ["Name", "Active Until"],
            itemsPerPage,
            currentPage
          )}
          {renderPagination(
            currentPage,
            Math.ceil(activeSubscriptions.length / itemsPerPage),
            setCurrentPage
          )}
        </Box>

        {/* Search Results Table */}
        {searchResults.length > 0 && (
          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              Search Results
            </Typography>
            <StyledTableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Hack Date</TableCell>
                    <TableCell>Leak Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedResults.map(renderSearchResultRow)}
                </TableBody>
              </Table>
            </StyledTableContainer>
            {renderPagination(currentPage, totalPages, setCurrentPage)}
          </Box>
        )}

        {/* Modal for displaying hack_info.id */}
        <Modal
          open={openModal}
          onClose={() => setOpenModal(false)}
          aria-labelledby="hack-info-modal"
          aria-describedby="hack-info-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography id="hack-info-modal" variant="h6" component="h2">
              Hack Info ID
            </Typography>
            <Typography id="hack-info-description" sx={{ mt: 2 }}>
              {selectedHackId}
            </Typography>
            <Button onClick={() => setOpenModal(false)}>Close</Button>
          </Box>
        </Modal>
      </Container>
    </ThemeProvider>
  );
}

export default Dashboard;
